import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { useThrottledCallback } from 'use-debounce'
import styles from './styles.module.css'
import AnimatedSection from '@/components/AnimatedSection'

interface Stage {
  number: string
  title: string
  description: string
}

const stages = [
  {
    number: '01',
    title: 'Скорость запуска',
    description: 'Запуск готового решения занимает несколько дней, тогда как разработка с нуля длится месяцы и требует время на тестирование.'
  },
  {
    number: '02',
    title: 'Снижение рисков',
    description: 'Вы получаете стабильный продукт, который уже прошёл проверку в реальных бизнес-условиях среди других наших клиентов.'
  },
  {
    number: '03',
    title: 'Стабильность',
    description: 'Все решения поставляются в контейнерах — это гарантирует, что система будет работать стабильно в любой среде.'
  }
]

const ReadySolutions: React.FC = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    align: 'start',
    breakpoints: { '(min-width: 1440px)': { active: false } },
  })

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])
  
  const onSelectThrottled = useThrottledCallback(onSelect, 100)

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect, onSelectThrottled])

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <section className={styles.realization}>
      <AnimatedSection animation="slideUp">
      <div className={styles.subtitle}>Готовые решения для быстрого старта</div>
      <h2 className={styles.title}>
        <span>Масштабируйте бизнес</span> без долгой разработки <span>за дни,</span> а не месяцы
      </h2>
      </AnimatedSection>
      
      <AnimatedSection animation="slideUp">
      <div className={styles.stagesSlider}>
        <div className={styles.stagesSliderContainer}>
          <div className={styles.embla}>
            <div className={styles.embla__viewport} ref={emblaRef}>
              <div className={styles.embla__container}>
                {stages.map((stage, index) => {
                  const imagePaths = [
                    '/images/it-solutions/ready_solutions_1.svg',
                    '/images/it-solutions/ready_solutions_2.svg',
                    '/images/it-solutions/ready_solutions_3.svg'
                  ]
                  
                  return (
                    <div key={index} className={styles.embla__slide}>
                      <div className={styles.stageCard}>
                        <div className={styles.stageNumber}>
                          {stage.number}
                        </div>
                        <div className={styles.stageImage}>
                          <Image
                            src={imagePaths[index]}
                            alt={stage.title}
                            width={310}
                            height={200}
                            className={styles.image}
                          />
                        </div>
                        <h3 className={styles.stageTitle}>{stage.title}</h3>
                        <p className={styles.stageDescription}>{stage.description}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            
            {!prevBtnDisabled && (
              <button 
                className={styles.embla__prev}
                onClick={onPrevButtonClick}
              >
                <Image
                  src="/images/portfolio/lk-agro-b2b/slider-button-left.svg"
                  alt="Previous"
                  width={34}
                  height={34}
                />
              </button>
            )}
            
            {!nextBtnDisabled && (
              <button 
                className={styles.embla__next}
                onClick={onNextButtonClick}
              >
                <Image
                  src="/images/portfolio/lk-agro-b2b/slider-button-right.svg"
                  alt="Next"
                  width={34}
                  height={34}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      </AnimatedSection>
    </section>
  )
}

ReadySolutions.displayName = 'ReadySolutions'

export default ReadySolutions